// La raffica per la STABILITÀ DEL CAMPO VISIVO (WP4): N scatti della stessa scacchiera, a pistola appoggiata, per vedere se la grandezza
// della scacchiera nell'immagine resta la stessa (ritaglio digitale, stabilizzazione, respiro del fuoco). L'analisi è `dim-fov-stability`
// (tools-py). Qui la parte che si può provare senza fotocamera: i nomi, la numerazione e il ciclo.

export const BURST_COUNT = 100;
/** Pausa fra uno scatto e il successivo: abbastanza da lasciar lavorare la fotocamera, abbastanza poco da stare dentro mezzo minuto. */
export const BURST_INTERVAL_MS = 300;
/** Il tempo per togliere la mano dallo schermo dopo aver premuto «Raffica»: toccare la pistola la sposta, e una pistola che si muove falsa la misura. */
export const BURST_START_DELAY_MS = 5000;

/**
 * L'identificativo di uno scatto della raffica, con i NUMERI CON GLI ZERI (`STAB-007`): l'analisi ordina i file per nome e la deriva nel tempo
 * dipende da quell'ordine. Con le lettere dei casi normali (`-a`, `-b`) oltre il 26° scatto l'ordine si romperebbe.
 */
export function burstId(prefix: string, n: number): string {
  return `${prefix}-${String(n).padStart(3, '0')}`;
}

/** Il prossimo numero libero per quel prefisso: una seconda raffica con lo stesso codice prosegue, non sovrascrive e non ricomincia da 1. */
export function nextBurstIndex(existingIds: readonly string[], prefix: string): number {
  const pattern = new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-(\\d{3,})$`);
  let max = 0;
  for (const id of existingIds) {
    const m = pattern.exec(id);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return max + 1;
}

export interface BurstOptions {
  count: number;
  intervalMs: number;
  /** Numero del primo scatto (da `nextBurstIndex`). */
  firstIndex: number;
  /** Attesa prima del primo scatto (default 0): il tempo di appoggiare la pistola. */
  startDelayMs?: number;
  /** Fa UNO scatto e lo salva. Se lancia, la raffica si ferma e l'errore sale. */
  shootOne: (index: number) => Promise<void>;
  sleep: (ms: number) => Promise<void>;
  signal?: AbortSignal;
  onProgress?: (done: number, total: number) => void;
}

export interface BurstResult {
  done: number;
  /** Vero se l'operatore ha interrotto prima della fine. */
  stopped: boolean;
}

/** Il ciclo: scatta `count` volte a intervalli regolari e si ferma subito se `signal` scatta. Gli scatti già fatti restano. */
export async function runBurst(o: BurstOptions): Promise<BurstResult> {
  let done = 0;
  if (o.startDelayMs) await o.sleep(o.startDelayMs);
  while (done < o.count) {
    if (o.signal?.aborted) return { done, stopped: true };
    await o.shootOne(o.firstIndex + done);
    done += 1;
    o.onProgress?.(done, o.count);
    // Nessuna pausa dopo l'ultimo: non si fa aspettare chi ha finito.
    if (done < o.count) await o.sleep(o.intervalMs);
  }

  return { done, stopped: false };
}
